import { useLayoutEffect } from 'react';

/**
 * Підбирає розмір шрифту, щоб текст влазив в один рядок: від `max` вниз до `min`.
 * Якщо й на `min` не влазить — лишає `min` і дозволяє перенос рядка.
 *
 * Елемент має бути блоком, що стискається (flex-елемент з min-width: 0 або width: 100%),
 * тоді «не влазить» = scrollWidth > clientWidth при white-space: nowrap.
 */
const fit = (el, max, min) => {
  el.style.whiteSpace = 'nowrap';

  for (let size = max; size >= min; size -= 1) {
    el.style.fontSize = `${size}px`;
    if (el.scrollWidth <= el.clientWidth) return;
  }

  el.style.fontSize = `${min}px`;
  el.style.whiteSpace = 'normal';
};

const useFitText = (ref, text, { max, min, enabled = true }) => {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return undefined;

    const run = () => fit(el, max, min);
    run();

    // Ширина картки міняється при повороті екрана / ресайзі; шрифт може догрузитись пізніше.
    const parent = el.parentElement;
    let lastWidth = parent ? parent.clientWidth : 0;
    const observer = typeof ResizeObserver !== 'undefined' && parent
      ? new ResizeObserver(() => {
        if (parent.clientWidth !== lastWidth) {
          lastWidth = parent.clientWidth;
          run();
        }
      })
      : null;
    if (observer) observer.observe(parent);

    let cancelled = false;
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (!cancelled) run(); });
    }

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      el.style.fontSize = '';
      el.style.whiteSpace = '';
    };
  }, [ref, text, max, min, enabled]);
};

export default useFitText;
